/* F1B.2B isolated test harness. Explicit dependencies/transport only; never loaded by the theme. */
(function (root, factory) {
  'use strict';
  if (typeof module === 'object' && module.exports) module.exports = factory(
    require('./nikuya-cart-resolver.js'), require('./nikuya-cart-adapter.js'), require('./nikuya-cart-financial.js'));
  else root.NikuyaCartControlledReconciliation = factory(root.NikuyaCartResolver, root.NikuyaCartAdapter, root.NikuyaCartFinancial);
})(typeof globalThis !== 'undefined' ? globalThis : this, function (Core, Adapter, Financial) {
  'use strict';
  const activeTransports = new WeakSet();
  const clone = value => JSON.parse(JSON.stringify(value));
  function stable(value) {
    if (Array.isArray(value)) return '[' + value.map(stable).join(',') + ']';
    if (value && typeof value === 'object') return '{' + Object.keys(value).sort().map(k => JSON.stringify(k) + ':' + stable(value[k])).join(',') + '}';
    return JSON.stringify(value);
  }
  const closed = (gates, keys = Core.caseMutationGates) => keys.filter(key => gates?.[key] !== true);
  function createIsolatedTestHarness({ mode, transport, gates, timeoutMs = 5000, allowOwnedCleanup = false, isAuthorized } = {}) {
    if ((mode !== 'ISOLATED_TEST_ONLY' && !(mode === 'AUTHORIZED_RUNTIME' && isAuthorized?.() === true)) || !Core || !Adapter || !Financial || !transport ||
        typeof transport.read !== 'function' || typeof transport.write !== 'function' ||
        !Number.isSafeInteger(timeoutMs) || timeoutMs < 1 || timeoutMs > 30000) {
      throw new TypeError('Explicit isolated transport and bounded timeout required');
    }
    const controls = Object.freeze({ ...gates });
    async function bounded(operation) {
      let timer;
      try { return await Promise.race([Promise.resolve().then(operation), new Promise((_, reject) => {
        timer = setTimeout(() => reject(new Error('TRANSPORT_TIMEOUT')), timeoutMs);
      })]); } finally { clearTimeout(timer); }
    }
    async function run({ caseSku, expectedProfile } = {}) {
      const result = { status: 'BLOCKED', confirmed: false, observedAchieved: false, writes: 0,
        transportStatus: 'NOT_ATTEMPTED', retryAutomatically: false, blockers: [], diagnostics: [] };
      const stop = code => { result.blockers.push({ code }); return result; };
      if (mode === 'AUTHORIZED_RUNTIME' && isAuthorized?.() !== true) return stop('ACTIVATION_CLOSED');
      if (closed(controls, allowOwnedCleanup ? Core.caseCleanupGates : Core.caseMutationGates).length) return stop('CONTROL_GATES_CLOSED');
      if (activeTransports.has(transport)) return stop('TRANSPORT_BUSY');
      activeTransports.add(transport);
      let pendingWrite = false, finished = false;
      try {
        let before;
        try { before = clone(await bounded(() => transport.read())); }
        catch (_) { return stop('INITIAL_READ_FAILED'); }
        if (closed(before?.gates, allowOwnedCleanup ? Core.caseCleanupGates : Core.caseMutationGates).length) return stop('SNAPSHOT_GATES_CLOSED');
        const plan = Core.planCaseReconciliation(before), state = plan.cases.find(c => c.caseSku === caseSku);
        const action = state?.actions.find(a => a.type !== 'NO_OP');
        const cleanup = allowOwnedCleanup && state?.cleanupOnly === true;
        const required = cleanup ? Core.caseCleanupGates : Core.caseMutationGates;
        if (closed(controls, required).length || closed(before?.gates, required).length) return stop('MUTATION_GATES_CLOSED');
        if (cleanup && (before.mappingVerified !== true || before.cart?.authoritative !== true ||
            before.snapshotRevision !== before.cart?.revision)) return stop('CLEANUP_AUTHORITY_REQUIRED');
        const prepared = Adapter.prepareCaseMutation(plan, caseSku, before.gates);
        if (!prepared.request) {
          if (prepared.status === 'NO_OP' && state.currentManagedQuantity > 0) {
            result.afterEvidence = Financial.verifyAcquisitionFinancialState({ ...before, expectedProfile, managedCaseTarget: { caseSku } });
            if (result.afterEvidence.status !== 'VERIFIED') return stop('RETAINED_FINANCIAL_EVIDENCE_REQUIRED');
          }
          result.status = prepared.status; return stop(prepared.code || 'NO_MUTATION_REQUIRED');
        }
        const preEvidence = Financial.verifyAcquisitionFinancialState({ ...before, expectedProfile,
          managedCaseTarget: state.currentManagedQuantity ? { caseSku } : undefined });
        result.beforeEvidence = preEvidence;
        if (!cleanup && preEvidence.status !== 'VERIFIED') return stop('PRE_WRITE_FINANCIAL_EVIDENCE_REQUIRED');
        const oldLine = before.cart.items.find(line => line.key === action.key);
        const expectedQuantity = action.type === 'ADD_MANAGED_CASE' ? state.currentManagedQuantity + action.quantity :
          state.currentManagedQuantity - oldLine.quantity + action.quantity;
        // The request exists only inside this invocation and is derived from its fresh snapshot.
        result.writes = 1;
        try {
          const reply = await bounded(async () => {
            pendingWrite = true;
            try { return await transport.write(clone(prepared.request), { snapshotRevision: before.cart.revision }); }
            finally {
              pendingWrite = false;
              if (finished) activeTransports.delete(transport);
            }
          });
          if (reply?.ok !== true) throw new Error('WRITE_NOT_ACKNOWLEDGED');
          result.transportStatus = 'ACKNOWLEDGED';
        } catch (_) {
          result.transportStatus = 'UNCERTAIN';
          result.diagnostics.push('WRITE_UNCERTAIN_NO_AUTOMATIC_RETRY');
        }
        let after;
        try { after = clone(await bounded(() => transport.read())); }
        catch (_) { result.status = 'UNCERTAIN'; return stop('POST_WRITE_READ_FAILED'); }
        result.snapshotRevision = after?.cart?.revision || null;
        if (!after || after.cart?.revision === before.cart.revision || closed(after.gates, required).length || after.mappingVerified !== true || after.snapshotRevision !== after.cart?.revision || after.cart?.authoritative !== true ||
            stable(after.revision) !== stable(before.revision) || stable(after.mappings) !== stable(before.mappings) ||
            stable(after.acquisition) !== stable(before.acquisition)) {
          result.status = 'UNCERTAIN'; return stop('STALE_OR_CHANGED_AUTHORITY');
        }
        const postPlan = Core.planCaseReconciliation(after), postState = postPlan.cases.find(c => c.caseSku === caseSku);
        if (!postState) { result.status = 'FAILED'; return stop('POST_WRITE_IDENTITY_INVALID'); }
        const untouched = (snapshot, managedKeys) => stable(snapshot.cart.items.filter(line => !managedKeys.includes(line.key)).map(line => ({
          sku: line.sku, product_id: String(line.product_id), variant_id: String(line.variant_id), quantity: line.quantity,
          properties: line.properties || {}, selling_plan_allocation: line.selling_plan_allocation || null,
          parent_relationship: line.parent_relationship || null, item_components: line.item_components || null
        })).map(stable).sort());
        if (postState.currentManagedQuantity !== expectedQuantity ||
            untouched(before, state.managedKeys) !== untouched(after, postState.managedKeys)) {
          result.status = result.transportStatus === 'UNCERTAIN' ? 'UNCERTAIN' : 'FAILED';
          return stop('POST_WRITE_QUANTITY_OR_PROTECTED_LINES_CHANGED');
        }
        const evidence = Financial.verifyAcquisitionFinancialState({ ...after, expectedProfile,
          managedCaseTarget: expectedQuantity > 0 ? { caseSku } : undefined });
        result.afterEvidence = evidence;
        const moreCleanup = cleanup && postState.cleanupOnly && postState.currentManagedQuantity > postState.desiredManagedQuantity;
        const removed = cleanup && expectedQuantity === 0 && postState.desiredManagedQuantity === 0;
        // An intermediate cleanup acknowledges only the safe quantity reduction, never unsafe retained pricing.
        if ((!moreCleanup && !removed && (evidence.status !== 'VERIFIED' ||
            (expectedQuantity > 0 && !evidence.casePricingVerified))) ||
            postState.blockers.some(b => !(cleanup && (b.code.startsWith('GATE_CLOSED:') || b.code.startsWith('BLOCKED_AVAILABILITY'))))) {
          result.status = 'FINANCIAL_FAILURE'; return stop('POST_WRITE_FINANCIAL_OR_SAFETY_VERIFICATION_FAILED');
        }
        result.cleanupOnly = cleanup;
        result.lifecycleComplete = !moreCleanup;
        result.observedAchieved = true;
        result.remainingActions = postPlan.actions.filter(a => a.type !== 'NO_OP').length;
        result.confirmed = result.transportStatus === 'ACKNOWLEDGED';
        result.status = result.confirmed ? 'CONFIRMED' : 'OBSERVED_ACHIEVED';
        return result;
      } finally {
        finished = true;
        // A timeout does not prove cancellation. Do not overlap a later invocation with a pending write.
        if (!pendingWrite) activeTransports.delete(transport);
      }
    }
    return Object.freeze({ run });
  }
  return Object.freeze({ createIsolatedTestHarness });
});
